import { create } from '@bufbuild/protobuf'
import _ from 'lodash'

import {
  type IRQCategorySeverity,
  IRQCategorySeveritySchema,
  type InherentRiskCategory,
  type InherentRiskCategoryDisplayOrder,
  type InherentRiskGroup,
  type OrgCompanyEngagement,
} from '@/gen/inventory/v1/company_service_pb'
import { RiskLevel } from '@/gen/inventory/v1/risk_pb'

import { sortRiskLevel } from '@/const/priority'

import type { IrqAnswerStore } from '@/lib/irq-answer-store/irq-answer-store.hook'
import { calculateInherentRisk } from '@/lib/risk/calculate-inherent-risk'

import {
  STEP_KIND,
  type SidebarRenderItem,
  type SidebarStep,
} from '@/pages/company-drawer/company-overview/set-irq-modal/types'

import { getInherentRisk } from '@/components/shared/risk-badge/utils'

// Order categories by severity (highest risk first) then alphabetically by name.
export const sortIrqCategorySeverities = (items: IRQCategorySeverity[]): IRQCategorySeverity[] =>
  [...items].sort((a, b) => {
    const bySeverity = sortRiskLevel(a.severity, b.severity)
    return bySeverity !== 0 ? bySeverity : a.category.localeCompare(b.category)
  })

/**
 * Computes per-category max severity from the local answer state,
 * respecting group visibility (conditional questions).
 */
export const computeLocalCategorySeverities = (
  categories: InherentRiskCategory[],
  { answers, visibleGroupIds }: Pick<IrqAnswerStore, 'answers' | 'visibleGroupIds'>,
): IRQCategorySeverity[] => {
  const selected = categories
    .filter((cat) => visibleGroupIds.has(cat.id))
    .flatMap((cat) => {
      const selectedIds = new Set(answers[cat.id]?.selectedOptionIds ?? [])
      return cat.items
        .filter((item) => item.id && selectedIds.has(item.id))
        .map((item) => ({ category: cat.category, severity: item.severity }))
    })

  return Object.entries(_.groupBy(selected, 'category'))
    .map(([category, items]) =>
      create(IRQCategorySeveritySchema, {
        category,
        severity: _.max(items.map((i) => i.severity)) ?? RiskLevel.UNSPECIFIED,
      }),
    )
    .filter(({ severity }) => severity !== RiskLevel.UNSPECIFIED)
}

export const computeSuggestedRiskLevel = ({
  irqItems,
  categories,
  localState,
}: {
  irqItems: InherentRiskGroup[]
  categories: InherentRiskCategory[]
  localState: Pick<IrqAnswerStore, 'answers' | 'visibleGroupIds'>
}): RiskLevel => {
  const catalogMap = new Map(categories.map(({ id, items }) => [id, items]))
  const localGroups = irqItems
    .filter((group) => localState.visibleGroupIds.has(group.id))
    .map((group) => {
      const selectedIds = localState.answers[group.id]?.selectedOptionIds ?? []
      return {
        ...group,
        inherentRiskItems: (catalogMap.get(group.id) || []).filter(
          (item) => item.id && selectedIds.includes(item.id),
        ),
      }
    })
  return calculateInherentRisk(localGroups)
}

// Effective engagement risk: validated overrides calculated when present.
const engagementRiskLevel = (
  engagement: Pick<OrgCompanyEngagement, 'calculatedRiskLevel' | 'validatedRiskLevel'>,
): RiskLevel => getInherentRisk(engagement.validatedRiskLevel, engagement.calculatedRiskLevel)

// Company IR in engagement mode = the engagement with the highest effective risk.
// Mirrors `calculateEnabledEngagementModeCompanyRisk` on the backend.
export const computeRiskLevelFromEngagements = (engagements: OrgCompanyEngagement[]): RiskLevel => {
  const top = sortEngagementsByRiskDesc(engagements)[0]
  return top ? engagementRiskLevel(top) : RiskLevel.UNSPECIFIED
}

// Sort engagements so the highest effective risk comes first.
export const sortEngagementsByRiskDesc = <
  T extends Pick<OrgCompanyEngagement, 'calculatedRiskLevel' | 'validatedRiskLevel'>,
>(
  engagements: T[],
): T[] =>
  [...engagements].sort((a, b) => sortRiskLevel(engagementRiskLevel(a), engagementRiskLevel(b)))

export const buildSidebarSteps = ({
  irqItems,
  categoryDisplayOrders,
  engagementModeEnabled,
  hasManagePermissions,
  isEngagementScoped = false,
  visibleGroupIds,
}: {
  irqItems: InherentRiskGroup[]
  categoryDisplayOrders: InherentRiskCategoryDisplayOrder[]
  engagementModeEnabled: boolean
  hasManagePermissions: boolean
  isEngagementScoped?: boolean
  visibleGroupIds: Set<string>
}): SidebarStep[] => {
  const orderLookup = new Map<string, number>(
    categoryDisplayOrders.map(({ category, displayOrder }) => [category, displayOrder]),
  )
  const orderedQuestions = [...irqItems].sort((a, b) => {
    const ao = orderLookup.get(a.category) ?? Number.MAX_SAFE_INTEGER
    const bo = orderLookup.get(b.category) ?? Number.MAX_SAFE_INTEGER
    if (ao !== bo) return ao - bo
    return a.displayOrder - b.displayOrder
  })

  if (engagementModeEnabled && !isEngagementScoped) {
    return [{ kind: STEP_KIND.ASSIGN }]
  }

  // Conditional questions stay in the list even when their condition isn't met; they render
  // disabled (isActive=false) rather than disappearing.
  const list: SidebarStep[] = [{ kind: STEP_KIND.OVERVIEW }]
  orderedQuestions.forEach((group) =>
    list.push({ kind: STEP_KIND.QUESTION, group, isActive: visibleGroupIds.has(group.id) }),
  )
  if (hasManagePermissions || isEngagementScoped) {
    list.push({ kind: STEP_KIND.ASSIGN })
  }
  return list
}

// Flattens steps into sidebar rows, adding section separators and category headers.
export const buildSidebarRenderItems = (steps: SidebarStep[]): SidebarRenderItem[] => {
  const items: SidebarRenderItem[] = []
  let lastCategory: string | undefined

  steps.forEach((step, index) => {
    switch (step.kind) {
      case STEP_KIND.OVERVIEW:
        items.push({ kind: STEP_KIND.OVERVIEW, index })
        lastCategory = undefined
        break

      case STEP_KIND.ASSIGN:
        if (items.length > 0) items.push({ kind: 'separator' })
        items.push({ kind: STEP_KIND.ASSIGN, index })
        lastCategory = undefined
        break

      case STEP_KIND.QUESTION: {
        if (lastCategory === undefined && items.length > 0) {
          items.push({ kind: 'separator' })
        }
        if (step.group.category !== lastCategory) {
          items.push({ kind: 'category-header', category: step.group.category })
          lastCategory = step.group.category
        }
        items.push({ kind: STEP_KIND.QUESTION, group: step.group, index, isActive: step.isActive })
        break
      }
    }
  })

  return items
}
